/**
 * The standing live-tolerance verdict (P344), read where a scene needs
 * to agree with the war room.
 *
 * Round 10 A1: the cascade in 3D closed "0 tolerances breached" while
 * the war room, on the same estate in live mode, reported two breached
 * NOW. Both were true and neither said which question it answered --
 * the scene projects what a propagation WOULD breach by a clock
 * position, the war room reports what IS breached at this moment. Two
 * questions rendered as one number is a contradiction a reader has no
 * way to resolve.
 *
 * So the scene reads the same endpoint the standing verdict comes
 * from, and prints it beside its own projection, each labelled.
 */
import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import { api, type ApiError } from "shared/api/client";

export interface LiveToleranceRow {
  ibs_service_id: string;
  ibs_code: string;
  ibs_name: string;
  mvb_flag: boolean;
  severity: string | null;
  projected_recovery_hours: number | null;
  impact_tolerance_hours: number;
  verdict: string;
  via: string | null;
  /** Round 20 U6/U7 -- the monitor's open breach finding and since when. */
  breach_finding_id?: string | null;
  breach_since?: string | null;
}

export interface LiveToleranceOut {
  generated_at: string;
  assessed: number;
  impacted: number;
  breached: number;
  mvb_breached: number;
  rows?: LiveToleranceRow[];
}

export const liveToleranceKey = ["twin", "tolerance", "live"] as const;

export function useLiveTolerance(): UseQueryResult<LiveToleranceOut, ApiError> {
  return useQuery({
    queryKey: liveToleranceKey,
    queryFn: () => api.get<LiveToleranceOut>("/v1/twin/tolerance/live"),
    // The monitor ticks on a cadence; a scene left open should not
    // drift away from the war room while somebody watches it.
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
}
